import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getPosts, addPost, updatePost, deletePost, uploadBlogImage, getPostCategories } from '../services/blogService';
import { getBlogCategories, addBlogCategory, updateBlogCategory, deleteBlogCategory } from '../services/blogCategoryService';
import { hasPermission } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { resolveAssetUrl } from '../utils/resolveUrl';
import API_BASE_URL from '../config';
import toast from 'react-hot-toast';
import './Admin.css';
import QuillEditor from '../components/QuillEditor';

function RichTextEditor({ value, onChange }) {
  const handleImageUpload = async (file) => {
    const res = await uploadBlogImage(file);
    if (res && res.url) return resolveAssetUrl(res.url);
    return null;
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean']
      ]
    }
  }), []);

  return (
    <div className="admin-form-group">
      <label className="admin-label">Content</label>
      <QuillEditor
        value={value || ''}
        onChange={onChange}
        modules={modules}
        onImageUpload={handleImageUpload}
        style={{ background: '#fff' }}
      />
    </div>
  );
}

export default function BlogsAdmin() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showCategoriesPanel, setShowCategoriesPanel] = useState(false);
  const [catForm, setCatForm] = useState({ id: null, name: '', description: '', imageFile: null, image: '', parentId: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ id: null, title: '', excerpt: '', content: '', categories: [], image: '', imageFile: null });
  const [newCatInput, setNewCatInput] = useState('');
  const [showPromoSectionInModal, setShowPromoSectionInModal] = useState(true);
  const [editorValue, setEditorValue] = useState('');
  const contentRef = useRef('');
  const navigate = useNavigate();

  // Blog Page Header & Promo Banner Settings State
  const [showPageHeaderModal, setShowPageHeaderModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState('header'); // 'header' | 'promo'
  const [pageHeaderTitle, setPageHeaderTitle] = useState(localStorage.getItem('blog_page_title') || 'Zbuloni artikujt më të fundit dhe këshillat nga ekspertët tanë!');
  const [pageHeaderSubtitle, setPageHeaderSubtitle] = useState(localStorage.getItem('blog_page_subtitle') || '');

  // Promo Banner Settings (for BlogPost.jsx sidebar card)
  const [promoBadge, setPromoBadge] = useState(localStorage.getItem('blog_promo_badge') || 'GLOBE ONLINE');
  const [promoTitle, setPromoTitle] = useState(localStorage.getItem('blog_promo_title') || 'Zbuloni ofertat më të mira në teknologji');
  const [promoDescription, setPromoDescription] = useState(localStorage.getItem('blog_promo_description') || 'Cilësia më e lartë, garanci zyrtare dhe dërgesa të shpejta në të gjithë Shqipërinë.');
  const [promoBtnText, setPromoBtnText] = useState(localStorage.getItem('blog_promo_btn_text') || 'Eksploro Produktet →');
  const [promoBtnLink, setPromoBtnLink] = useState(localStorage.getItem('blog_promo_btn_link') || '/products');
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    contentRef.current = form.content || '';
  }, [form.content]);

  useEffect(() => {
    if (!hasPermission('admin.blogs')) {
      navigate('/login');
      return;
    }
    load();
    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/settings`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.blog_page_title) {
          setPageHeaderTitle(data.blog_page_title);
          localStorage.setItem('blog_page_title', data.blog_page_title);
        }
        if (data && data.blog_page_subtitle !== undefined) {
          setPageHeaderSubtitle(data.blog_page_subtitle);
          localStorage.setItem('blog_page_subtitle', data.blog_page_subtitle);
        }
        if (data && data.blog_promo_badge) {
          setPromoBadge(data.blog_promo_badge);
          localStorage.setItem('blog_promo_badge', data.blog_promo_badge);
        }
        if (data && data.blog_promo_title) {
          setPromoTitle(data.blog_promo_title);
          localStorage.setItem('blog_promo_title', data.blog_promo_title);
        }
        if (data && data.blog_promo_description !== undefined) {
          setPromoDescription(data.blog_promo_description);
          localStorage.setItem('blog_promo_description', data.blog_promo_description);
        }
        if (data && data.blog_promo_btn_text) {
          setPromoBtnText(data.blog_promo_btn_text);
          localStorage.setItem('blog_promo_btn_text', data.blog_promo_btn_text);
        }
        if (data && data.blog_promo_btn_link) {
          setPromoBtnLink(data.blog_promo_btn_link);
          localStorage.setItem('blog_promo_btn_link', data.blog_promo_btn_link);
        }
      }
    } catch (_) {}
  };

  const saveSettings = async (e) => {
    if (e) e.preventDefault();
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        blog_page_title: pageHeaderTitle,
        blog_page_subtitle: pageHeaderSubtitle,
        blog_promo_badge: promoBadge,
        blog_promo_title: promoTitle,
        blog_promo_description: promoDescription,
        blog_promo_btn_text: promoBtnText,
        blog_promo_btn_link: promoBtnLink
      };
      const res = await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      localStorage.setItem('blog_page_title', pageHeaderTitle);
      localStorage.setItem('blog_page_subtitle', pageHeaderSubtitle);
      localStorage.setItem('blog_promo_badge', promoBadge);
      localStorage.setItem('blog_promo_title', promoTitle);
      localStorage.setItem('blog_promo_description', promoDescription);
      localStorage.setItem('blog_promo_btn_text', promoBtnText);
      localStorage.setItem('blog_promo_btn_link', promoBtnLink);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
      if (res.ok) {
        toast.success('Konfigurimet e blogut u ruajtën me sukses!');
        setShowPageHeaderModal(false);
      } else {
        toast.success('Konfigurimet u ruajtën.');
        setShowPageHeaderModal(false);
      }
    } catch (err) {
      localStorage.setItem('blog_page_title', pageHeaderTitle);
      localStorage.setItem('blog_page_subtitle', pageHeaderSubtitle);
      localStorage.setItem('blog_promo_badge', promoBadge);
      localStorage.setItem('blog_promo_title', promoTitle);
      localStorage.setItem('blog_promo_description', promoDescription);
      localStorage.setItem('blog_promo_btn_text', promoBtnText);
      localStorage.setItem('blog_promo_btn_link', promoBtnLink);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
      toast.success('Konfigurimet u ruajtën lokalisht.');
      setShowPageHeaderModal(false);
    } finally {
      setSavingSettings(false);
    }
  };

  const load = async () => {
    setLoading(true);
    const p = await getPosts();
    setPosts(p || []);
    const cats = await getBlogCategories();
    setCategories(cats || []);
    setLoading(false);
  };

  // Flattened known category names from categories tree and posts
  const allKnownCategories = useMemo(() => {
    const list = new Set();
    const addCat = (c) => {
      if (c && c.name) list.add(c.name.trim());
      if (Array.isArray(c.subcategories)) c.subcategories.forEach(addCat);
    };
    categories.forEach(addCat);
    posts.forEach(p => {
      getPostCategories(p).forEach(cat => list.add(cat));
    });
    return Array.from(list).filter(Boolean);
  }, [categories, posts]);

  const startAdd = () => {
    setForm({ id: null, title: '', excerpt: '', content: '', categories: [], image: '', imageFile: null });
    setNewCatInput('');
    contentRef.current = '';
    setEditorValue('');
    setShowPromoSectionInModal(true);
    loadSettings();
    setShowForm(true);
  };

  const startEdit = (post) => {
    const postCats = getPostCategories(post);
    setForm({
      id: post.id,
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      categories: postCats,
      image: post.image,
      imageFile: null
    });
    setNewCatInput('');
    contentRef.current = post.content || '';
    setEditorValue(post.content || '');
    setShowPromoSectionInModal(true);
    loadSettings();
    setShowForm(true);
  };

  const handleAddCustomCategory = () => {
    const trimmed = newCatInput.trim();
    if (!trimmed) return;
    if (!form.categories.includes(trimmed)) {
      setForm(prev => ({ ...prev, categories: [...prev.categories, trimmed] }));
    }
    setNewCatInput('');
  };

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('excerpt', form.excerpt);
    const contentToSave = String(contentRef.current ?? editorValue ?? form.content ?? '');
    fd.append('content', contentToSave);
    fd.append('category', (form.categories || []).join(', '));
    if (form.imageFile) fd.append('image', form.imageFile);
    if (form.id) await updatePost(form.id, fd);
    else await addPost(fd);

    // Save Promo Banner Settings simultaneously
    try {
      const token = localStorage.getItem('token');
      const payload = {
        blog_page_title: pageHeaderTitle,
        blog_page_subtitle: pageHeaderSubtitle,
        blog_promo_badge: promoBadge,
        blog_promo_title: promoTitle,
        blog_promo_description: promoDescription,
        blog_promo_btn_text: promoBtnText,
        blog_promo_btn_link: promoBtnLink
      };
      await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      localStorage.setItem('blog_promo_badge', promoBadge);
      localStorage.setItem('blog_promo_title', promoTitle);
      localStorage.setItem('blog_promo_description', promoDescription);
      localStorage.setItem('blog_promo_btn_text', promoBtnText);
      localStorage.setItem('blog_promo_btn_link', promoBtnLink);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
    } catch (_) {}

    await load();
    setShowForm(false);
    toast.success(form.id ? 'Artikulli dhe konfigurimet u përditësuan!' : 'Artikulli dhe konfigurimet u shtuan me sukses!');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await deletePost(id);
    await load();
  };

  if (loading) return <div className="admin-text" style={{ padding: 20 }}>Loading...</div>;

  return (
    <div>
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Titulli aktual i faqes:</span>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pageHeaderTitle}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="admin-btn admin-btn-secondary" onClick={() => setShowPageHeaderModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Blog & Banner Settings
            </button>
            <button className="admin-btn admin-btn-primary" onClick={startAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add Post
            </button>
            <button className="admin-btn admin-btn-secondary" onClick={async () => { setShowCategoriesPanel(s => !s); setCatForm({ id: null, name: '', description: '', imageFile: null, image: '', parentId: '' }); setCategories(await getBlogCategories()); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              {showCategoriesPanel ? 'Close Categories' : 'Manage Categories'}
            </button>
          </div>
        </div>
        {posts.length === 0 ? <div className="admin-text">No blog posts found.</div> : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th width="80">Image</th>
                  <th>Title</th>
                  <th>Categories</th>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => {
                  const postCats = getPostCategories(post);
                  return (
                    <tr key={post.id}>
                      <td>
                        <div style={{ width: 60, height: 40, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {post.image ? <img src={resolveAssetUrl(post.image)} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 9, color: '#94a3b8' }}>N/A</span>}
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{post.title}</td>
                      <td>
                        {postCats.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {postCats.map(cat => (
                              <span key={cat} style={{ background: '#e0f2fe', color: '#007da5', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                                {cat}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>&mdash;</span>
                        )}
                      </td>
                      <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => startEdit(post)} style={{ marginRight: 8 }}>Edit</button>
                        <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => handleDelete(post.id)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 999999, padding: 20, boxSizing: 'border-box' }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: 800, maxHeight: '90vh', overflowY: 'auto', margin: 0, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0 }}>{form.id ? 'Edit Post' : 'New Post'}</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}>&times;</button>
            </div>
            <form onSubmit={submit}>
              <div className="admin-form-group">
                <label className="admin-label">Title</label>
                <input className="admin-input" placeholder='Post Title' value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>

              {/* Multi-Category Selection */}
              <div className="admin-form-group">
                <label className="admin-label">Kategoritë (Zgjidhni një ose disa kategori)</label>
                
                {/* Active Selected Category Badges */}
                {form.categories && form.categories.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    {form.categories.map(cat => (
                      <span
                        key={cat}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          background: '#007da5',
                          color: '#ffffff',
                          padding: '6px 12px',
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          boxShadow: '0 1px 3px rgba(0, 125, 165, 0.2)'
                        }}
                      >
                        <span>{cat}</span>
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, categories: prev.categories.filter(c => c !== cat) }))}
                          style={{
                            background: 'rgba(255,255,255,0.25)',
                            border: 'none',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 14,
                            lineHeight: 1
                          }}
                          title={`Hiq ${cat}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 8, fontStyle: 'italic' }}>
                    Asnjë kategori e zgjedhur (artikulli do të shfaqet te "Të Tjera").
                  </div>
                )}

                {/* Available Categories Toggle Buttons */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    padding: 12,
                    background: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 10,
                    maxHeight: 180,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {allKnownCategories.map(catName => {
                    const isChecked = form.categories?.includes(catName);
                    return (
                      <button
                        key={catName}
                        type="button"
                        onClick={() => {
                          setForm(prev => {
                            const current = prev.categories || [];
                            const next = isChecked ? current.filter(c => c !== catName) : [...current, catName];
                            return { ...prev, categories: next };
                          });
                        }}
                        style={{
                          padding: '7px 14px',
                          borderRadius: 8,
                          border: isChecked ? '1.5px solid #007da5' : '1px solid #cbd5e1',
                          background: isChecked ? '#e0f2fe' : '#ffffff',
                          color: isChecked ? '#007da5' : '#334155',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          minHeight: 36,
                          touchAction: 'manipulation',
                          userSelect: 'none',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 700 }}>{isChecked ? '✓' : '+'}</span>
                        <span>{catName}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Add Custom Category Input */}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    className="admin-input"
                    placeholder="Shto një kategori të re..."
                    value={newCatInput}
                    onChange={(e) => setNewCatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomCategory();
                      }
                    }}
                    style={{ flex: '1 1 180px', fontSize: 13, padding: '8px 12px', minHeight: 38 }}
                  />
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary"
                    onClick={handleAddCustomCategory}
                    style={{ padding: '8px 16px', minHeight: 38, whiteSpace: 'nowrap', fontWeight: 600 }}
                  >
                    + Shto Kategori
                  </button>
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Featured Image</label>
                <input className="admin-input" type='file' accept='image/*' onChange={(e) => setForm({ ...form, imageFile: e.target.files?.[0] || null })} style={{ padding: 8 }} />
                {form.image && !form.imageFile && <div style={{ marginTop: 8 }}><img src={resolveAssetUrl(form.image)} alt='preview' style={{ height: 48, borderRadius: 4, objectFit: 'cover' }} /></div>}
              </div>

              <RichTextEditor value={editorValue} onChange={(val) => { contentRef.current = val; setEditorValue(val); }} />

              {/* Sidebar Promo Card Editor directly inside the Post Edit/Add modal */}
              <div style={{ marginTop: 22, border: '1.5px solid #cbd5e1', borderRadius: 12, overflow: 'hidden', background: '#f8fafc' }}>
                <div
                  onClick={() => setShowPromoSectionInModal(s => !s)}
                  style={{
                    padding: '12px 18px',
                    background: '#f1f5f9',
                    borderBottom: showPromoSectionInModal ? '1px solid #cbd5e1' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16 }}>📢</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>
                        Edito Kartën Reklamuese të Sidebar-it (Promo Banner)
                      </div>
                      <div style={{ fontSize: 11.5, color: '#64748b' }}>
                        Përshtatni tekstin, titullin dhe linkun e kartës promocionale në sidebar-in e artikullit
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#007da5' }}>
                    {showPromoSectionInModal ? 'Fshih ▲' : 'Shfaq & Edito ▼'}
                  </span>
                </div>

                {showPromoSectionInModal && (
                  <div style={{ padding: 18, background: '#ffffff', display: 'grid', gap: 14 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div className="admin-form-group" style={{ margin: 0 }}>
                        <label className="admin-label">Badge i sipërm (Teksti portokalli)</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={promoBadge}
                          onChange={(e) => setPromoBadge(e.target.value)}
                          placeholder="psh. GLOBE ONLINE"
                        />
                      </div>
                      <div className="admin-form-group" style={{ margin: 0 }}>
                        <label className="admin-label">Titulli i Kartës Reklamuese</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={promoTitle}
                          onChange={(e) => setPromoTitle(e.target.value)}
                          placeholder="psh. Zbuloni ofertat më të mira në teknologji"
                        />
                      </div>
                    </div>

                    <div className="admin-form-group" style={{ margin: 0 }}>
                      <label className="admin-label">Përshkrimi i Kartës</label>
                      <textarea
                        className="admin-textarea"
                        rows={2}
                        value={promoDescription}
                        onChange={(e) => setPromoDescription(e.target.value)}
                        placeholder="psh. Cilësia më e lartë, garanci zyrtare dhe dërgesa të shpejta..."
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div className="admin-form-group" style={{ margin: 0 }}>
                        <label className="admin-label">Teksti i Butonit</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={promoBtnText}
                          onChange={(e) => setPromoBtnText(e.target.value)}
                          placeholder="psh. Eksploro Produktet →"
                        />
                      </div>
                      <div className="admin-form-group" style={{ margin: 0 }}>
                        <label className="admin-label">Linku i Butonit</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={promoBtnLink}
                          onChange={(e) => setPromoBtnLink(e.target.value)}
                          placeholder="psh. /products ose https://..."
                        />
                      </div>
                    </div>

                    {/* Live Preview */}
                    <div style={{ marginTop: 4 }}>
                      <label className="admin-label" style={{ marginBottom: 6 }}>Pamja Paraprake (Live Preview në Sidebar):</label>
                      <div
                        style={{
                          background: 'linear-gradient(135deg, #007da5 0%, #005a77 100%)',
                          borderRadius: 12,
                          padding: 16,
                          color: '#ffffff',
                          maxWidth: 360,
                          boxShadow: '0 4px 12px rgba(0, 125, 165, 0.2)'
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#f48d21', marginBottom: 4 }}>
                          {promoBadge || 'GLOBE ONLINE'}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>
                          {promoTitle || 'Zbuloni ofertat më të mira'}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.4, marginBottom: 10 }}>
                          {promoDescription || 'Cilësia më e lartë dhe dërgesa të shpejta.'}
                        </div>
                        <div
                          style={{
                            display: 'inline-block',
                            background: '#f48d21',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: 12,
                            padding: '6px 14px',
                            borderRadius: 6
                          }}
                        >
                          {promoBtnText || 'Eksploro Produktet →'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="admin-actions" style={{ marginTop: 20 }}>
                <button type='button' className="admin-btn admin-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type='submit' className="admin-btn admin-btn-primary">Save Post & Banner</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Blog categories management modal */}
      {showCategoriesPanel && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 999999, padding: 20, boxSizing: 'border-box' }}>
          <div className="admin-card" style={{ width: '90%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', margin: 0, padding: 0, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0 }}>Manage Blog Categories</h3>
              <button onClick={() => { setShowCategoriesPanel(false); setCatForm({ id: null, name: '', description: '', imageFile: null, image: '', parentId: '' }); }} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}>&times;</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) 320px', gap: 24 }}>
              {/* Category List */}
              <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
                {categories.length === 0 ? <div className="admin-text">No categories</div> : (
                  (() => {
                    const renderNode = (node, depth = 0) => (
                      <div key={node.id} style={{ marginLeft: depth * 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                          <div style={{ width: 48, height: 48, background: '#f8fafc', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                            {node.image ? <img src={resolveAssetUrl(node.image)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{node.name}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>{node.description}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setCatForm({ id: node.id, name: node.name || '', description: node.description || '', imageFile: null, image: node.image || '', parentId: node.parentId || '' })}>Edit</button>
                            <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={async () => { if (!window.confirm('Delete this category?')) return; await deleteBlogCategory(node.id); setCategories(await getBlogCategories()); }}>Delete</button>
                          </div>
                        </div>
                        {Array.isArray(node.subcategories) && node.subcategories.length > 0 && (
                          <div style={{ marginTop: 8, display: 'grid', gap: 8, borderLeft: '2px solid #e2e8f0', marginLeft: 24, paddingLeft: 12 }}>
                            {node.subcategories.map(child => renderNode(child, 0))}
                          </div>
                        )}
                      </div>
                    );
                    return categories.map(c => renderNode(c, 0));
                  })()
                )}
              </div>

              {/* Edit Form Sidebar */}
              <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px solid #e2e8f0', height: 'fit-content' }}>
                <h4 className="admin-subtitle" style={{ marginBottom: 16 }}>{catForm.id ? 'Edit Category' : 'Add New Category'}</h4>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const fd = new FormData();
                  fd.append('name', catForm.name);
                  fd.append('description', catForm.description);
                  if (catForm.imageFile) fd.append('image', catForm.imageFile);
                  if (catForm.parentId) fd.append('parentId', catForm.parentId);
                  if (catForm.id) await updateBlogCategory(catForm.id, fd); else await addBlogCategory(fd);
                  setCategories(await getBlogCategories());
                  setCatForm({ id: null, name: '', description: '', imageFile: null, image: '', parentId: '' });
                }}>
                  <div className="admin-form-group">
                    <label className="admin-label">Name</label>
                    <input className="admin-input" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} required />
                  </div>
                  <div className="admin-form-group">
                    <label className="admin-label">Description</label>
                    <textarea className="admin-textarea" value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} rows={3} />
                  </div>
                  <div className="admin-form-group">
                    <label className="admin-label">Parent Category</label>
                    <select className="admin-select" value={catForm.parentId || ''} onChange={(e) => setCatForm({ ...catForm, parentId: e.target.value || '' })}>
                      <option value="">— None (Top Level) —</option>
                      {categories.map(cat => {
                        if (cat.id === catForm.id) return null;
                        const renderOpts = (node, prefix = '') => {
                          const opts = [];
                          opts.push(<option key={node.id} value={node.id} disabled={node.id === catForm.id}>{prefix + node.name}</option>);
                          if (Array.isArray(node.subcategories) && node.subcategories.length) {
                            node.subcategories.forEach(child => opts.push(...renderOpts(child, prefix + '— ')));
                          }
                          return opts;
                        };
                        return renderOpts(cat);
                      })}
                    </select>
                  </div>
                  <div className="admin-form-group">
                    <label className="admin-label">Image</label>
                    <input className="admin-input" type="file" accept="image/*" onChange={(e) => setCatForm({ ...catForm, imageFile: e.target.files?.[0] || null })} style={{ padding: 8 }} />
                  </div>
                  <div className="admin-actions" style={{ marginTop: 16 }}>
                    <button type="button" className="admin-btn admin-btn-secondary" style={{ flex: 1 }} onClick={() => setCatForm({ id: null, name: '', description: '', imageFile: null, image: '', parentId: '' })}>Reset</button>
                    <button type="submit" className="admin-btn admin-btn-primary" style={{ flex: 1 }}>{catForm.id ? 'Save' : 'Create'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Blog & Banner Settings Modal */}
      {showPageHeaderModal && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 999999, padding: 20, boxSizing: 'border-box' }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: 680, margin: 0, padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#007da5' }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Konfigurimet e Blogut & Sidebar Banner
              </h3>
              <button onClick={() => setShowPageHeaderModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}>&times;</button>
            </div>

            {/* Modal Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
              <button
                type="button"
                onClick={() => setSettingsTab('header')}
                style={{
                  padding: '6px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: settingsTab === 'header' ? '#007da5' : '#f1f5f9',
                  color: settingsTab === 'header' ? '#ffffff' : '#334155',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                1. Titulli Kryesor i Faqes (H1)
              </button>
              <button
                type="button"
                onClick={() => setSettingsTab('promo')}
                style={{
                  padding: '6px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: settingsTab === 'promo' ? '#007da5' : '#f1f5f9',
                  color: settingsTab === 'promo' ? '#ffffff' : '#334155',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                2. Karta Reklamuese në Sidebar
              </button>
            </div>

            <form onSubmit={saveSettings}>
              {settingsTab === 'header' ? (
                <div>
                  <div className="admin-form-group" style={{ marginBottom: 16 }}>
                    <label className="admin-label">H1 Page Title (Titulli kryesor në krye të faqes së blogjeve)</label>
                    <textarea
                      className="admin-textarea"
                      rows={3}
                      value={pageHeaderTitle}
                      onChange={(e) => setPageHeaderTitle(e.target.value)}
                      placeholder="psh. Zbuloni artikujt më të fundit dhe këshillat nga ekspertët tanë!"
                      required
                    />
                    <span style={{ fontSize: 12, color: '#64748b', marginTop: 4, display: 'block' }}>
                      Ky titull shfaqet në madhësinë <code>&lt;h1&gt;</code> në krye të faqes <code>/blog</code> dhe <code>/blogs</code>.
                    </span>
                  </div>

                  <div className="admin-form-group" style={{ marginBottom: 20 }}>
                    <label className="admin-label">Nëntitulli (Opsional)</label>
                    <input
                      className="admin-input"
                      type="text"
                      value={pageHeaderSubtitle}
                      onChange={(e) => setPageHeaderSubtitle(e.target.value)}
                      placeholder="psh. Eksploroni artikujt tanë sipas kategorive"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="admin-form-group" style={{ marginBottom: 14 }}>
                    <label className="admin-label">Badge i sipërm (Teksti portokalli)</label>
                    <input
                      className="admin-input"
                      type="text"
                      value={promoBadge}
                      onChange={(e) => setPromoBadge(e.target.value)}
                      placeholder="psh. GLOBE ONLINE"
                      required
                    />
                  </div>

                  <div className="admin-form-group" style={{ marginBottom: 14 }}>
                    <label className="admin-label">Titulli i Kartës Reklamuese</label>
                    <input
                      className="admin-input"
                      type="text"
                      value={promoTitle}
                      onChange={(e) => setPromoTitle(e.target.value)}
                      placeholder="psh. Zbuloni ofertat më të mira në teknologji"
                      required
                    />
                  </div>

                  <div className="admin-form-group" style={{ marginBottom: 14 }}>
                    <label className="admin-label">Përshkrimi i Kartës</label>
                    <textarea
                      className="admin-textarea"
                      rows={2}
                      value={promoDescription}
                      onChange={(e) => setPromoDescription(e.target.value)}
                      placeholder="psh. Cilësia më e lartë, garanci zyrtare dhe dërgesa të shpejta në të gjithë Shqipërinë."
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                    <div className="admin-form-group">
                      <label className="admin-label">Teksti i Butonit</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={promoBtnText}
                        onChange={(e) => setPromoBtnText(e.target.value)}
                        placeholder="psh. Eksploro Produktet →"
                        required
                      />
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-label">Linku i Butonit</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={promoBtnLink}
                        onChange={(e) => setPromoBtnLink(e.target.value)}
                        placeholder="psh. /products ose https://..."
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="admin-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowPageHeaderModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={savingSettings}>
                  {savingSettings ? 'Duke ruajtur...' : 'Ruaj Të Gjitha Ndryshimet'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
